import type { MedplumClient } from '@medplum/core';
import type { Appointment } from '@medplum/fhirtypes';

export const PORTAL_RESCHEDULE_ORIGINAL_APPOINTMENT_EXT =
  'https://unify.health/fhir/StructureDefinition/portal-reschedule-original-appointment';

export function getPortalRescheduleOriginalAppointmentId(appt: Appointment): string | undefined {
  const ext = appt.extension?.find((e) => e.url === PORTAL_RESCHEDULE_ORIGINAL_APPOINTMENT_EXT);
  const ref = ext?.valueReference?.reference;
  if (!ref?.startsWith('Appointment/')) return undefined;
  return ref.slice('Appointment/'.length);
}

function patientRefFromAppointment(appt: Appointment): string | undefined {
  for (const participant of appt.participant ?? []) {
    const ref = participant.actor?.reference;
    if (ref?.startsWith('Patient/')) return ref;
  }
  return undefined;
}

function matchesPortalRescheduleHold(
  apt: Appointment,
  originalAppointmentId: string,
  requestedStart: string,
  requestedEnd: string,
): boolean {
  return (
    getPortalRescheduleOriginalAppointmentId(apt) === originalAppointmentId
    && apt.start === requestedStart
    && apt.end === requestedEnd
    && Boolean(apt.id)
  );
}

export function isRecoverableRescheduleSubmitError(message: string): boolean {
  return (
    message.includes('Only booked appointments can be rescheduled')
    || message.includes('That time was just taken')
  );
}

/** Finds an existing portal reschedule proposed hold for the cancelled original + requested slot. */
export async function findPortalProposedRescheduleHold(
  medplum: MedplumClient,
  originalAppointmentId: string,
  requestedStart: string,
  requestedEnd: string,
  practitionerId?: string,
): Promise<string | undefined> {
  let patientRef: string | undefined;
  try {
    const original = await medplum.readResource('Appointment', originalAppointmentId);
    patientRef = patientRefFromAppointment(original);
  } catch {
    return undefined;
  }

  const matcher = (apt: Appointment): boolean =>
    matchesPortalRescheduleHold(apt, originalAppointmentId, requestedStart, requestedEnd);

  if (patientRef) {
    const patientCandidates = await medplum.searchResources('Appointment', {
      patient: patientRef,
      status: 'proposed',
      _count: '50',
    });
    const fromPatient = patientCandidates.find(matcher);
    if (fromPatient?.id) return fromPatient.id;
  }

  if (practitionerId) {
    const providerCandidates = await medplum.searchResources('Appointment', {
      actor: `Practitioner/${practitionerId}`,
      status: 'proposed',
      date: `lt${requestedEnd}`,
      _count: '50',
    });
    const fromProvider = providerCandidates.find(matcher);
    if (fromProvider?.id) return fromProvider.id;
  }

  return undefined;
}
